import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Drawer, List, ListItem, ListItemIcon, ListItemText, Box, useTheme, Grow, useMediaQuery, IconButton, Divider } from '@mui/material';
import { NavLink, useLocation } from 'react-router-dom';
import hiloadLogo from '../../assets/images/hiloadLogo.jpeg';
import { useTranslation } from 'react-i18next';
import { getMenuItems } from './menuItems';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import MenuIcon from '@mui/icons-material/Menu';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { AuthContext } from '../../provider/authProvider';


const Sidebar = () => {
    const location = useLocation();
    const theme = useTheme();
    const { i18n, t } = useTranslation();

    const { user } = useContext(AuthContext);
    const userPermissions = user?.permissions || [];

    const menuItems = getMenuItems(t, userPermissions);
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));
    const [open, setOpen] = useState(true);

    const toggleDrawer = () => setOpen(!open);

    const isActive = (menuItem) => {
        if (menuItem.path === location.pathname) return true;
        if (menuItem.items) {
            return menuItem.items.some(subItem =>
                location.pathname.startsWith(subItem.path.replace(':id', ''))
            );
        }
        return false;
    };

    useEffect(() => {
        setOpen(!isTablet);
    }, [isTablet]);

    const fontSize = i18n.language === 'en' ? 15 : 14;
    const memoizedIcons = useMemo(() => {
        const icons = {};
        menuItems.forEach(item => {
            icons[item.text] = React.cloneElement(item.icon, { fontSize: "small" });
            item.items?.forEach(sub => {
                icons[sub.text] = React.cloneElement(sub.icon, { fontSize: "small" });
            });
        });
        return icons;
    }, [menuItems]);


    return (
        <>
            <Drawer
                variant='permanent'
                anchor={i18n.language === 'en' ? 'left' : 'right'}
                transitionDuration={300}
                sx={{
                    width: open ? 240 : 60,
                    transition: 'width 0.5s ease-in-out',
                    [`& .MuiDrawer-paper`]: {
                        width: open ? 240 : 60,
                        boxSizing: 'border-box',
                        transition: 'width 0.5s ease-in-out',
                    },
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: open ? 'space-between' : 'center',
                        alignItems: 'center',
                        px: 1.5
                    }}
                >
                    {open && <img src={hiloadLogo} alt="Logo" width={130} />}
                    <IconButton onClick={toggleDrawer}
                        sx={{ height: 30, my: 1, color: theme.palette.text.primary }}
                    >
                        {open ? (isTablet &&
                            (i18n.language === 'en' ?
                                <KeyboardArrowLeftIcon /> : <KeyboardArrowRightIcon />)
                        ) : <MenuIcon />}
                    </IconButton>
                </Box>

                <Divider />

                <List>
                    {menuItems.map((item) =>
                        open ? (
                            <ListItem
                                button="true"
                                key={item.text}
                                component={NavLink}
                                to={item.path}
                                sx={{
                                    position: 'relative',
                                    padding: 0,
                                    width: '100%',
                                    '&:hover': {
                                        backgroundColor: 'transparent',
                                    },
                                }}
                            >

                                <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}>
                                    <Box sx={{
                                        width: 4,
                                        borderTopRightRadius: i18n.language === 'en' ? 100 : 0,
                                        borderBottomRightRadius: i18n.language === 'en' ? 100 : 0,
                                        borderTopLeftRadius: i18n.language === 'en' ? 0 : 100,
                                        borderBottomLeftRadius: i18n.language === 'en' ? 0 : 100,
                                        backgroundColor: isActive(item) ? theme.palette.primary.main : 'transparent',
                                    }}>
                                    </Box>
                                    <Box>
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            width: '220px',
                                            height: '40px',
                                            borderRadius: 1.1,
                                            backgroundColor: isActive(item) ? theme.palette.primary.main : 'transparent',
                                            py: 0.8
                                        }}>
                                            <ListItemIcon
                                                sx={{
                                                    color: isActive(item) ? 'white' : theme.palette.text.primary,
                                                    minWidth: 0,
                                                    mx: 2,
                                                }}
                                            >{memoizedIcons[item.text]}
                                            </ListItemIcon>
                                            <ListItemText sx={{
                                                color: isActive(item) ? 'white' : theme.palette.text.primary,
                                                textAlign: i18n.language === 'en' ? 'left' : 'right'
                                            }}
                                                primaryTypographyProps={{
                                                    fontSize: fontSize,
                                                }}
                                                primary={item.text}
                                            />
                                            {
                                                item.items && <ListItemIcon
                                                    sx={{
                                                        color: isActive(item) ? 'white' : theme.palette.text.primary,
                                                        minWidth: 0,
                                                        mx: 0.2,
                                                    }}
                                                >
                                                    {
                                                        isActive(item) ?
                                                            <ExpandLessIcon fontSize='small' /> :
                                                            <ExpandMoreIcon fontSize='small' />
                                                    }
                                                </ListItemIcon>
                                            }
                                        </Box>

                                        {
                                            isActive(item) &&
                                            <Grow
                                                in={location.pathname === item.path || (item.items && item.items.length > 0)}
                                                style={{ transformOrigin: '0 0 0' }}
                                                {...(location.pathname === item.path || item.items ? { timeout: 400 } : {})}
                                            >
                                                <List sx={{
                                                    padding: 0,
                                                    borderRadius: 1.1,
                                                    border: item.items ? 1 : 0,
                                                    py: item.items ? 1 : 0,
                                                    borderColor: theme.palette.customGrey.borderInput,
                                                }}>
                                                    {item.items?.map((secoundItem, i) => (

                                                        secoundItem.show && <ListItem
                                                            button="true"
                                                            key={secoundItem.text}
                                                            component={NavLink}
                                                            to={secoundItem.path}
                                                            sx={{
                                                                position: 'relative',
                                                                padding: 0,
                                                                mx: 1,
                                                                width: '100%',
                                                                '&:hover': {
                                                                    backgroundColor: 'transparent',
                                                                },
                                                            }}
                                                        >

                                                            <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}>
                                                                <Box sx={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    width: '200px',
                                                                    height: '40px',
                                                                    borderRadius: 1.1,
                                                                    backgroundColor: 'transparent',
                                                                    py: 0.8,
                                                                }}>
                                                                    <ListItemIcon
                                                                        sx={{
                                                                            color: !isActive(secoundItem) ? theme.palette.text.primary : theme.palette.primary.main,
                                                                            minWidth: 0,
                                                                            mx: 2,
                                                                        }}
                                                                    >
                                                                        {memoizedIcons[secoundItem.text]}
                                                                    </ListItemIcon>
                                                                    <ListItemText sx={{
                                                                        color: !isActive(secoundItem) ? theme.palette.text.primary : theme.palette.primary.main,
                                                                        fontSize: 16,
                                                                        textAlign: i18n.language === 'en' ? 'left' : 'right'
                                                                    }}
                                                                        primaryTypographyProps={{
                                                                            fontSize: fontSize,
                                                                        }}
                                                                        primary={secoundItem.text}
                                                                    />
                                                                </Box>

                                                                <Box width={'4px'}></Box>
                                                            </Box>
                                                        </ListItem>

                                                    )
                                                    )}
                                                </List>
                                            </Grow>
                                        }
                                    </Box>
                                    <Box width={'4px'}></Box>
                                </Box>




                            </ListItem>
                        ) :

                            // just icons

                            (
                                <ListItem
                                    button="true"
                                    key={item.text}
                                    component={NavLink}
                                    to={item.path}
                                    sx={{
                                        position: 'relative',
                                        padding: 0,
                                        width: '100%',
                                        '&:hover': {
                                            backgroundColor: 'transparent',
                                        },
                                    }}
                                >
                                    <Box sx={{ display: 'flex', width: '100%', mx: 1, justifyContent: 'start' }}>
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: '40px',
                                            height: '40px',
                                            borderRadius: 1.1,
                                            backgroundColor: isActive(item) ? theme.palette.primary.main : 'transparent',
                                        }}>
                                            <ListItemIcon
                                                sx={{
                                                    color: isActive(item) ? 'white' : theme.palette.text.primary,
                                                    minWidth: 0,
                                                }}
                                            > {memoizedIcons[item.text]}
                                            </ListItemIcon>
                                        </Box>
                                    </Box>

                                </ListItem>
                            )
                    )}
                </List>
            </Drawer>

        </>


    );
};


export default Sidebar;
